import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileCheck2, CheckCircle2, ArrowRight, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { products } from '../mock/data';
import { productDetails } from '../mock/productDetails';
import { useI18n } from '../context/I18nContext';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const product = products.find((p) => String(p.id) === String(id));
  const detail = productDetails[id];

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Package className="w-12 h-12 text-slate-400 mx-auto" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Product not found</h1>
          <Link to="/" className="mt-4 inline-block text-[#f7a11a] font-semibold">{t('cta.backToHome')}</Link>
        </div>
      </div>
    );
  }

  const related = products.filter((p) => p.id !== product.id).slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <div className="h-20" />

      {/* Hero */}
      <section className="relative bg-[#0b2545] overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${product.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545] via-[#0b2545]/95 to-[#0b2545]/70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-white/70 hover:text-[#f7a11a] text-sm mb-8">
            <ArrowLeft className="w-4 h-4" /> {t('cta.backToHome')}
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#f7a11a]/10 border border-[#f7a11a]/30 mb-6">
            <span className="text-[#f7a11a] text-xs font-bold tracking-widest">PRODUCT #{String(product.id).padStart(2,'0')}</span>
          </div>
          <h1 className="text-white font-black leading-[0.95] tracking-tight text-4xl lg:text-6xl max-w-3xl">{product.title}</h1>
          <p className="mt-6 text-slate-200 text-lg max-w-2xl leading-relaxed">{product.desc}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button onClick={() => { navigate('/'); setTimeout(() => document.querySelector('#quote')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="bg-[#f7a11a] hover:bg-[#e69108] text-slate-900 font-semibold h-12 px-6 rounded-none tracking-wide text-sm">
              {t('cta.requestQuote')} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <div className="aspect-square overflow-hidden bg-slate-100">
                <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-10">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('productPage.specs')}</span>
              <div className="mt-4 border border-slate-200">
                {detail.specs.map(([k, v], i) => (
                  <div key={i} className={`grid grid-cols-3 gap-4 px-5 py-4 ${i % 2 ? 'bg-slate-50' : 'bg-white'}`}>
                    <div className="font-semibold text-slate-700 text-sm">{k}</div>
                    <div className="col-span-2 text-slate-900 text-sm">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('productPage.applications')}</span>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {detail.applications.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200">
                    <CheckCircle2 className="w-5 h-5 text-[#f7a11a] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-800 text-sm">{a}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('productPage.standards')}</span>
              <div className="mt-4 flex flex-wrap gap-2">
                {detail.standards.map((s) => (
                  <div key={s} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0b2545] text-white text-sm font-semibold">
                    <FileCheck2 className="w-4 h-4 text-[#f7a11a]" /> {s}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 lg:py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h3 className="text-3xl lg:text-4xl font-black text-slate-900 mb-10">{t('productPage.related')}</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group relative aspect-square overflow-hidden bg-slate-100">
                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545] via-[#0b2545]/40 to-transparent" />
                <div className="absolute inset-0 p-5 flex items-end">
                  <h4 className="text-white font-bold text-base lg:text-lg">{p.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
